import type { AxiosInstance } from 'axios';
import axios from 'axios';

import { LogoutEvent } from './api.events';
import { TokenPersistService } from './token-persist.service';
import type { RefreshTokensUseCase } from './use-cases';

export class TokensService extends TokenPersistService {
  public constructor(
    private readonly refreshTokensUseCase: RefreshTokensUseCase
  ) {
    super();
  }

  public use(httpClient: AxiosInstance) {
    this.attachAccessTokenToRequest(httpClient);
    this.refreshTokensOn401(httpClient);
  }

  public async refreshTokens() {
    const { refreshToken } = this.getTokens();

    const newTokens = await this.refreshTokensUseCase.execute(refreshToken!);

    this.setTokens(newTokens.accessToken, newTokens.refreshToken);

    return {
      accessToken: newTokens.accessToken,
      refreshToken: newTokens.refreshToken,
    };
  }

  private attachAccessTokenToRequest(httpClient: AxiosInstance) {
    httpClient.interceptors.request.use(
      async (request) => {
        const { accessToken } = this.getTokens();

        if (accessToken) {
          request.headers.Authorization = `Bearer ${accessToken}`;
        }

        return request;
      },
      (error) => Promise.reject(error)
    );
  }

  private refreshTokensOn401(httpClient: AxiosInstance) {
    httpClient.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (!axios.isAxiosError(error)) {
          return Promise.reject(error);
        }

        if (error.response?.data.message === 'INVALID_REFRESH_TOKEN') {
          this.logout();

          return Promise.reject(error);
        }

        if (error.response?.status === 401) {
          const { refreshToken } = this.getTokens();

          if (refreshToken) {
            await this.refreshTokens().catch((error) => {
              this.logout();

              return Promise.reject(error);
            });

            return httpClient.request(error.config!);
          } else {
            this.logout();

            return Promise.reject(error);
          }
        }

        return Promise.reject(error);
      }
    );
  }

  private logout() {
    this.removeTokens();
    document.dispatchEvent(new LogoutEvent());
  }
}
